import { useRovingFocus, useRovingTabStop } from "@/hooks/use-roving-focus";
import { useSelectionIrisOrigin } from "@/hooks/use-selection-iris-origin";
import { cn } from "@/lib/cn";
import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from "react";
import { Button } from "./Button";
import { CheckIndicator, RadioIndicator } from "./Indicators";
import { SelectionIris } from "./SelectionIris";
import { SurfaceProvider } from "./Surface";
import { Text } from "./Typography";

type SelectableCardGroupContextValue = { value: string | null; onValueChange: (value: string) => void };

const SelectableCardGroupContext = createContext<SelectableCardGroupContextValue | null>(null);

type SelectableCardDensity = "md" | "sm";

const ICON_TONE = { primary: "text-primary", secondary: "text-secondary" } as const;

const DENSITY: Record<SelectableCardDensity, { root: string; title: "bold" | "semibold" }> = {
  md: { root: "gap-4 p-4", title: "bold" },
  sm: { root: "gap-3 p-3", title: "semibold" },
};

type SelectionSource =
  | { selected: boolean; onSelect: () => void; value?: undefined }
  | { value: string; selected?: undefined; onSelect?: () => void };

/** Props of `SelectableCard`: standalone (`selected` + `onSelect`) or a `value` inside `SelectableCardGroup`. */
export type SelectableCardProps = SelectionSource & {
  /** Shape of the trailing mark. Purely visual: see `inGroup` for semantics. */
  indicator?: "radio" | "checkbox";
  /**
   * True when the card is one option of a `role="radiogroup"` (announced as a radio). A standalone card
   * stays a toggle button (`aria-pressed`): a lone radio is invalid ARIA and loses the on/off state.
   */
  inGroup?: boolean;
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Trailing content drawn before the indicator (a price, a badge). */
  meta?: ReactNode;
  /** Lets a long title wrap instead of truncating to one line. */
  wrapTitle?: boolean;
  /** `sm` tightens padding and gaps for dense lists (checkout channels, time slots). */
  density?: SelectableCardDensity;
  /** `selection`: regular title at rest, semibold once picked (time slots), so weight marks the choice. */
  titleWeight?: "density" | "selection";
  disabled?: boolean;
  /** Palette of the selected state. */
  tone?: "primary" | "secondary";
  "aria-label"?: string;
  className?: string;
};

/**
 * A pickable card: optional icon (pass `Icon tone="current"`: the card paints it grey at rest, `tone` when picked), title, supporting line, trailing meta and a radio/checkbox mark.
 * Built on `Button variant="surface"`; the selected tint opens as a `SelectionIris` from the tap point; with `value` inside a SelectableCardGroup it is a roving radio.
 */
export function SelectableCard({
  selected: selectedProp,
  onSelect,
  value,
  indicator: indicatorProp,
  inGroup: inGroupProp = false,
  icon,
  title,
  subtitle,
  meta,
  wrapTitle = false,
  density = "md",
  titleWeight = "density",
  disabled = false,
  tone = "primary",
  "aria-label": ariaLabel,
  className,
}: SelectableCardProps) {
  const group = useContext(SelectableCardGroupContext);
  const grouped = group !== null && value !== undefined;
  const selected = grouped ? group.value === value : Boolean(selectedProp);
  const inGroup = grouped || inGroupProp;
  const indicator = indicatorProp ?? (grouped ? "radio" : "checkbox");
  const roving = grouped ? { "data-value": value } : {};
  const ariaState = inGroup ? { role: "radio", "aria-checked": selected } : { "aria-pressed": selected };
  const Indicator = indicator === "radio" ? RadioIndicator : CheckIndicator;
  const layout = DENSITY[density];
  const iris = useSelectionIrisOrigin();

  return (
    <Button
      variant="surface"
      tone={tone}
      size="auto"
      align="between"
      fullWidth
      weight="normal"
      selected={selected && !disabled}
      disabled={disabled}
      onClick={() => {
        if (grouped) group.onValueChange(value);
        onSelect?.();
      }}
      onPointerDownCapture={iris.onPointerDownCapture}
      aria-label={ariaLabel}
      {...ariaState}
      {...roving}
      className={cn(
        "relative isolate rounded-card bg-card text-left shadow-card",
        layout.root,
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
    >
      <SelectionIris selected={selected && !disabled} origin={iris.origin} tone={tone} />
      <SurfaceProvider surface="card">
        {icon ? (
          <span className={cn("shrink-0 transition-colors duration-base ease-standard", selected ? ICON_TONE[tone] : "text-card-text/60")}>
            {icon}
          </span>
        ) : null}

        <span className="flex min-w-0 flex-1 flex-col">
          <Text
            as="span"
            size="base"
            weight={titleWeight === "selection" ? (selected ? "semibold" : "normal") : layout.title}
            tone="default"
            leading="tight"
            truncate={!wrapTitle}
            className={wrapTitle ? "break-words" : undefined}
          >
            {title}
          </Text>
          {subtitle ? (
            <Text as="span" size="sm" tone="muted" leading="snug" className="mt-0.5">
              {subtitle}
            </Text>
          ) : null}
        </span>

        {meta ? <span className="shrink-0">{meta}</span> : null}

        <Indicator checked={selected} tone={tone} size={density === "sm" ? "sm" : "md"} />
      </SurfaceProvider>
    </Button>
  );
}

/** Props of `SelectableCardGroup`; name it with `label` or `aria-labelledby`. */
export type SelectableCardGroupProps = Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue" | "dir"> & {
  value: string | null;
  onValueChange: (value: string) => void;
  label?: string;
  orientation?: "vertical" | "horizontal";
  children: ReactNode;
};

/** `role="radiogroup"` of `SelectableCard value=` children with roving focus (arrows select, Home/End jump). */
export const SelectableCardGroup = forwardRef<HTMLDivElement, SelectableCardGroupProps>(function SelectableCardGroup(
  { value, onValueChange, label, orientation = "vertical", className, onKeyDown, children, ...rest },
  ref,
) {
  const rove = useRovingFocus<HTMLButtonElement>({
    selector: '[role="radio"]:not(:disabled)',
    onMove: (card) => {
      if (card.dataset.value !== undefined && card.dataset.value !== value) onValueChange(card.dataset.value);
    },
  });
  const groupRef = useRovingTabStop(ref);
  return (
    <SelectableCardGroupContext.Provider value={{ value, onValueChange }}>
      <div
        {...rest}
        ref={groupRef}
        role="radiogroup"
        aria-label={rest["aria-labelledby"] ? undefined : label}
        aria-orientation={orientation}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          rove(event);
        }}
        className={cn("flex gap-3", orientation === "vertical" ? "flex-col" : "flex-row flex-wrap", className)}
      >
        {children}
      </div>
    </SelectableCardGroupContext.Provider>
  );
});
